import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  signal,
} from '@angular/core';

@Directive({
  selector: '[scrollFade]',
  standalone: true,
  host: {
    class: 'scroll-fade',
    '[class.can-scroll-left]': 'canScrollLeft()',
    '[class.can-scroll-right]': 'canScrollRight()',
    '[style.--scroll-fade-color]': 'scrollFadeColor()',
    '[style.--scroll-fade-size]': 'scrollFadeSize()',
  },
})
export class ScrollFadeDirective implements AfterViewInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  readonly scrollFadeDisabled = input(false);
  readonly scrollFadeColor = input<string | null>(null);
  readonly scrollFadeSize = input<string | null>(null);

  protected readonly canScrollLeft = signal(false);
  protected readonly canScrollRight = signal(false);

  ngAfterViewInit(): void {
    const scrollElement = this.getScrollElement();
    if (!scrollElement) {
      return;
    }

    const update = () => this.updateScrollState(scrollElement);
    update();

    scrollElement.addEventListener('scroll', update, { passive: true });

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(scrollElement);

    this.destroyRef.onDestroy(() => {
      scrollElement.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    });
  }

  private getScrollElement(): HTMLElement | null {
    const target = this.host.nativeElement.querySelector('[scrollFadeTarget]');
    return (target ?? this.host.nativeElement.firstElementChild) as HTMLElement | null;
  }

  private updateScrollState(element: HTMLElement): void {
    if (this.scrollFadeDisabled()) {
      this.canScrollLeft.set(false);
      this.canScrollRight.set(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = element;
    const hasOverflow = scrollWidth > clientWidth + 1;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    this.canScrollLeft.set(hasOverflow && !atStart);
    this.canScrollRight.set(hasOverflow && !atEnd);
  }
}
